// =============================================================================
// VandieZ admin-web -- Auth context + provider + hook
// =============================================================================
// Single source of truth for the logged-in user + JWT pair. Persists tokens
// to localStorage so a page refresh doesn't drop the session, and rehydrates
// on mount by calling /auth/me. If /me returns 401 the persisted tokens are
// dropped silently -- expired tokens are an expected state, not an error.
//
// Sprint 1 scope: no automatic refresh-token rotation on 401. That lands
// alongside the protected admin endpoints in a later sprint (see
// docs/VandieZ_Build_05_Admin_Deployment.md).
// =============================================================================

import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

import { authApi } from "@/api/client";
import { ApiError, type UserRead } from "@/types/auth";

const STORAGE_KEY = "vandiez.admin.auth.v1";

interface PersistedAuth {
  accessToken: string;
  refreshToken: string;
}

function readPersisted(): PersistedAuth | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) {return null;}
    const parsed = JSON.parse(raw) as Partial<PersistedAuth>;
    if (
      typeof parsed.accessToken === "string" &&
      typeof parsed.refreshToken === "string"
    ) {
      return { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken };
    }
    return null;
  } catch {
    return null;
  }
}

function writePersisted(auth: PersistedAuth | null): void {
  if (auth === null) {
    window.localStorage.removeItem(STORAGE_KEY);
  } else {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
  }
}

export interface AuthState {
  user: UserRead | null;
  accessToken: string | null;
  refreshToken: string | null;
  /** True while the initial /auth/me rehydration is in flight. */
  isInitializing: boolean;
}

export interface AuthContextValue extends AuthState {
  /** Replace the stored tokens + user (called after successful OTP verify). */
  setSession: (args: {
    user: UserRead;
    accessToken: string;
    refreshToken: string;
  }) => void;
  /** Clear local state + call /auth/logout. Errors during logout are swallowed. */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    refreshToken: null,
    isInitializing: true,
  });

  // On mount: try to rehydrate from localStorage.
  useEffect(() => {
    const persisted = readPersisted();
    if (persisted === null) {
      setState((s) => ({ ...s, isInitializing: false }));
      return;
    }

    const controller = new AbortController();
    authApi
      .me(persisted.accessToken, controller.signal)
      .then((user) => {
        setState({
          user,
          accessToken: persisted.accessToken,
          refreshToken: persisted.refreshToken,
          isInitializing: false,
        });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {return;}
        // 401 / 403 / network failure -> drop persisted tokens, show login.
        if (err instanceof ApiError) {
          // Expected: token was expired/revoked. Quietly clear.
          writePersisted(null);
        }
        setState({
          user: null,
          accessToken: null,
          refreshToken: null,
          isInitializing: false,
        });
      });

    return () => { controller.abort(); };
  }, []);

  const setSession = useCallback(
    (args: {
      user: UserRead;
      accessToken: string;
      refreshToken: string;
    }): void => {
      writePersisted({
        accessToken: args.accessToken,
        refreshToken: args.refreshToken,
      });
      setState({
        user: args.user,
        accessToken: args.accessToken,
        refreshToken: args.refreshToken,
        isInitializing: false,
      });
    },
    [],
  );

  const logout = useCallback(async (): Promise<void> => {
    const access = state.accessToken;
    const refresh = state.refreshToken;
    // Optimistic: clear local state first so the UI updates instantly even
    // if the server call hangs.
    writePersisted(null);
    setState({
      user: null,
      accessToken: null,
      refreshToken: null,
      isInitializing: false,
    });
    if (access !== null) {
      try {
        await authApi.logout(access, refresh ?? undefined);
      } catch {
        // Logout is idempotent server-side; swallowing matches the
        // "user wants out" semantics.
      }
    }
  }, [state.accessToken, state.refreshToken]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, setSession, logout }),
    [state, setSession, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Co-located with AuthProvider so consumers get a single import; splitting
// the hook out is a fast-refresh micro-optimization with no real benefit
// (auth context rarely changes during a HMR session).
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuth() must be used inside <AuthProvider>.");
  }
  return ctx;
}
